import type { Locale } from "@/lib/i18n";

export interface BlogPost {
  /** URL slug in the post's OWN locale (matches the route param for this page). */
  slug: string;
  /** TR filename-based slug — stable cross-locale identity, used to find alternates. */
  canonicalSlug: string;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  author: string;
  content: string;
}

// Basit frontmatter ayrıştırıcı
function parseFrontmatter(raw: string): { metadata: Record<string, string>; content: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { metadata: {}, content: raw };

  const metadata: Record<string, string> = {};
  for (const line of match[1].split("\n")) {
    const sep = line.indexOf(": ");
    if (sep > 0) {
      const key = line.slice(0, sep).trim();
      const val = line.slice(sep + 2).trim().replace(/^"|"$/g, "");
      metadata[key] = val;
    }
  }

  return { metadata, content: match[2].trim() };
}

// Vite glob import'u ile tüm blog dosyalarını yükle (TR varsayılan + <slug>.<locale>.md çevirileri)
const modules = import.meta.glob("/src/content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: false,
});

// Desteklenen çeviri son ekleri (tr hariç — tr zaten <slug>.md, son ek taşımaz)
const LOCALE_SUFFIX = /^(.*)\.(en|de|es|ru|fr|it)$/;

/**
 * Her TR dosyasını (<slug>.md) kanonik kayıt kabul eder. Bir çeviri dosyası
 * (<slug>.<locale>.md) VARSA o dilde gerçek çeviri kullanılır; YOKSA o yazı
 * o dilin listesinden tamamen ÇIKARILIR (Türkçe içeriği "çevrilmiş" gibi
 * göstermemek için — bkz. memory feedback-siralama-kaybi / kopya içerik riski,
 * 16 Eylül 2026 SEO denetimi bulgusu).
 */
async function loadAll(): Promise<{ tr: Map<string, BlogPost>; translations: Map<string, Map<string, BlogPost>> }> {
  const tr = new Map<string, BlogPost>();
  const translations = new Map<string, Map<string, BlogPost>>(); // locale -> slug -> post

  for (const [path, loader] of Object.entries(modules)) {
    const filename = path.split("/").pop()?.replace(/\.md$/, "") ?? "";
    const localeMatch = filename.match(LOCALE_SUFFIX);
    const raw = (await loader()) as string;
    const { metadata, content } = parseFrontmatter(raw);

    const canonicalSlug = localeMatch ? localeMatch[1] : filename;
    const post: BlogPost = {
      // Çeviri dosyası kendi dilinde bir `slug:` alanı tanımlıyorsa (localize
      // edilmiş URL, ör. İngilizce yazı için İngilizce slug) o kullanılır;
      // tanımlanmamışsa geriye dönük uyumluluk için TR slug'ı miras alır
      // (16 Eylül 2026 GSC denetimi: TR slug'ın tüm dillerde tekrarı Google'a
      // "kopya sayfa" sinyali veriyordu, bkz memory anadoluterra-blog-i18n).
      slug: metadata.slug || canonicalSlug,
      canonicalSlug,
      title: metadata.title ?? filename,
      date: metadata.date ?? "",
      excerpt: metadata.excerpt ?? "",
      image: metadata.image ?? "/og-image.jpg",
      author: metadata.author ?? "Anadolu Terra",
      content,
    };

    if (localeMatch) {
      const [, , locale] = localeMatch;
      if (!translations.has(locale)) translations.set(locale, new Map());
      translations.get(locale)!.set(canonicalSlug, post);
    } else {
      tr.set(post.slug, post);
    }
  }

  return { tr, translations };
}

export async function getAllPosts(locale: Locale = "tr"): Promise<BlogPost[]> {
  const { tr, translations } = await loadAll();
  const localeMap = translations.get(locale);

  const posts: BlogPost[] = locale === "tr"
    ? [...tr.values()]
    : [...tr.keys()]
        .filter((slug) => localeMap?.has(slug))
        .map((slug) => localeMap!.get(slug)!);

  posts.sort((a, b) => b.date.localeCompare(a.date));
  return posts;
}

export async function getPostBySlug(slug: string, locale: Locale = "tr"): Promise<BlogPost | null> {
  const { tr, translations } = await loadAll();
  if (locale === "tr") return tr.get(slug) ?? null;
  // Çeviri haritası TR canonicalSlug ile keylenir, ama URL'deki slug artık
  // o dilin KENDİ (lokalize) slug'ı olabilir — key ile değil, post.slug alanıyla eşleştir.
  const localeMap = translations.get(locale);
  if (!localeMap) return null;
  for (const post of localeMap.values()) {
    if (post.slug === slug) return post;
  }
  return null;
}

/**
 * Bir yazının GERÇEKTEN çevirisi olan dillerde kullandığı URL slug'ı (tr her
 * zaman dahil, yazı varsa). Header'daki dil değiştiricinin ve hreflang
 * alternate etiketlerinin, çevirisi olmayan bir dile "kırık" yönlendirme
 * yapmasını veya yanlış (paylaşılan TR) slug'a işaret etmesini engellemek
 * için kullanılır (bkz. src/lib/pageLocales.tsx, src/components/site/Seo.tsx).
 */
export async function getLocalizedSlugsForSlug(canonicalSlug: string): Promise<Partial<Record<Locale, string>>> {
  const { tr, translations } = await loadAll();
  if (!tr.has(canonicalSlug)) return {};
  const result: Partial<Record<Locale, string>> = { tr: canonicalSlug };
  for (const [locale, map] of translations) {
    const post = map.get(canonicalSlug);
    if (post) result[locale as Locale] = post.slug;
  }
  return result;
}
